import { atom, createStore, useAtomValue, useSetAtom } from "jotai";
import { useLayoutEffect } from "react";
import Storage, { StorageOptions } from "./Storage";

type StoredAtom<T> = ReturnType<typeof atom<T>>;

const storedAtomsStore = createStore();
const atomCollection: Record<
    string,
    {
        referenceCount: number;
        atom: StoredAtom<unknown>;
    }
> = {};

function findInCollection<T>(
    key: string,
    defaultValue: T,
): { referenceCount: number; atom: StoredAtom<T> } {
    if (!atomCollection[key]) {
        atomCollection[key] = {
            referenceCount: 0,
            atom: atom(Storage.load(key, defaultValue)) as StoredAtom<unknown>,
        };
    }
    return atomCollection[key] as {
        referenceCount: number;
        atom: StoredAtom<T>;
    };
}

function useStoredAtomValue<T>(key: string, defaultValue: T) {
    const state = useAtomValue(findInCollection(key, defaultValue).atom, {
        store: storedAtomsStore,
    });

    useLayoutEffect(() => {
        const collectionItem = findInCollection(key, defaultValue);
        collectionItem.referenceCount++;
        return () => {
            collectionItem.referenceCount -= 1;
            if (collectionItem.referenceCount === 0) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete atomCollection[key];
            }
        };
    }, [key]);

    return state;
}

function useStoredSetAtom<T>(
    key: string,
    defaultValue: T,
    options: StorageOptions = {},
) {
    const setState = useSetAtom(findInCollection(key, defaultValue).atom, {
        store: storedAtomsStore,
    });

    const overrideSetState: typeof setState = (value) => {
        Storage.save(key, value, options);
        setState(value);
    };

    return overrideSetState;
}

export function useStoredAtom<T>(
    key: string,
    defaultValue: T,
    options: StorageOptions = {},
) {
    return [
        useStoredAtomValue(key, defaultValue),
        useStoredSetAtom(key, defaultValue, options),
    ] as const;
}

export function storedAtom<T>(
    key: string,
    defaultValue: T,
    options: StorageOptions = {},
) {
    return {
        atom: () => findInCollection(key, defaultValue).atom,
        use: () => useStoredAtom(key, defaultValue, options),
        useValue: () => useStoredAtomValue(key, defaultValue),
        useSet: () => useStoredSetAtom(key, defaultValue, options),
    };
}

window.addEventListener("storage", (event) => {
    if (!event.key) return;
    const atom = atomCollection[event.key]?.atom;
    if (!atom) return;
    try {
        const newValue = event.newValue ? JSON.parse(event.newValue) : null;
        storedAtomsStore.set(atom, newValue);
    } catch (error) {
        console.error(
            `Error parsing stored atom value for key "${event.key}"`,
            error,
        );
    }
});
